import { usePageGuideVisit, useTourProgress } from "@/hooks/use-organisation";
import { useControllableState } from "@radix-ui/react-use-controllable-state";
import {
  Activity,
  ChevronLeft,
  ChevronRight,
  Files,
  Globe,
  Inbox,
  Mailbox,
  Plug,
  Tag,
  Users,
  Video,
  X,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import {
  pageVisitGuideConfig,
  PageVisitGuideType,
} from "./page-visit-guide-config";

type PageVisitGuideModalProps = {
  open?: boolean;
  defaultOpen?: boolean;
  onClose?: () => void;
  type: PageVisitGuideType;
};

const pageVisitGuideIcons: Record<PageVisitGuideType, React.ReactNode> = {
  mailbox: <Mailbox className="text-white" size={18} />,
  sender: <Users className="text-white" size={18} />,
  postmaster: <Globe className="text-white" size={18} />,
  emailHealth: <Activity className="text-white" size={18} />,
  inboxPlacement: <Inbox className="text-white" size={18} />,
  integration: <Plug className="text-white" size={18} />,
  templates: <Files className="text-white" size={18} />,
  team: <Users className="text-white" size={18} />,
  role: <Users className="text-white" size={18} />,
  mailboxLabel: <Tag className="text-white" size={18} />,
  senderProfileLabel: <Tag className="text-white" size={18} />,
  emailHealthLabel: <Tag className="text-white" size={18} />,
  postmasterLabel: <Tag className="text-white" size={18} />,
  templateLabel: <Tag className="text-white" size={18} />,
};

const InitialPageVisitGuideModal: React.FC<PageVisitGuideModalProps> = ({
  open: openProp,
  defaultOpen,
  onClose,
  type = "mailbox",
}) => {
  const [open, setOpen] = useControllableState({
    defaultProp: defaultOpen ?? false,
    prop: openProp,
    onChange: onClose,
  });

  const { data: tourProgress } = useTourProgress();
  const pageGuideVisitMutation = usePageGuideVisit();

  const [videoError, setVideoError] = useState(false);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);

  const pageConfig = pageVisitGuideConfig[type];

  if (!pageConfig?.videos?.length) return null;

  const nextVideo = () => {
    setCurrentVideoIndex((prev) => (prev + 1) % pageConfig.videos.length);
  };

  const previousVideo = () => {
    setCurrentVideoIndex(
      (prev) => (prev - 1 + pageConfig.videos.length) % pageConfig.videos.length
    );
  };

  const handleClose = () => {
    setOpen(false);
    pageGuideVisitMutation.mutate(type);
  };

  useEffect(() => {
    if (!tourProgress) return;

    if (!tourProgress?.pageGuideVisits[type]) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [setOpen, tourProgress, type]);

  if (!open) return null;

  return (
    <div className="fixed sm:bottom-6 sm:right-6 bottom-4 right-4 left-4 sm:left-auto bg-background shadow-md rounded-lg sm:max-w-sm sm:w-full z-50">
      <button
        onClick={handleClose}
        className="absolute top-4 right-4 text-white  flex items-center justify-center z-10"
      >
        <X className="size-4" />
      </button>

      <div className="w-full rounded-2xl shadow-xl p-0 overflow-hidden border-0">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-700 dark:from-blue-700 to-blue-500 dark:to-blue-800 px-4 py-4 relative">
          <div className="flex items-center gap-4">
            <div className="bg-white/15 p-2.5 rounded-lg">
              {pageVisitGuideIcons[type]}
            </div>
            <h2 className="text-white text-base font-semibold">
              {pageConfig.title || "Tutorial Guide"}
            </h2>
          </div>
        </div>

        {/* Video carousel section */}
        <div className="px-4 py-4 bg-white dark:bg-secondary/30">
          <h3 className="text-gray-900 dark:text-white font-semibold text-base">
            {pageConfig.videos[currentVideoIndex].title}
          </h3>
          <p className="text-muted-foreground text-sm mb-4">
            {pageConfig.videos[currentVideoIndex].description}
          </p>

          <div className="relative">
            {/* Video container */}
            <div className="rounded-xl overflow-hidden bg-gray-50 dark:bg-secondary border border-gray-100 dark:border-border shadow-sm aspect-video">
              {!videoError ? (
                <iframe
                  width="100%"
                  height="100%"
                  src={pageConfig.videos[currentVideoIndex].url}
                  title={pageConfig.videos[currentVideoIndex].title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full"
                  onError={() => setVideoError(true)}
                />
              ) : (
                <div className="rounded-xl bg-gray-50 py-14 flex flex-col items-center justify-center">
                  <Video className="text-blue-500 mb-4" size={48} />
                  <span className="text-gray-800 font-medium text-lg">
                    Video unavailable
                  </span>
                </div>
              )}
            </div>

            {/* Navigation buttons - Only show for mailbox type with multiple videos */}
            {pageConfig.videos.length > 1 && (
              <>
                <button
                  onClick={previousVideo}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/30 hover:bg-white/50 p-2 rounded-full shadow-lg transition-all"
                  aria-label="Previous video"
                >
                  <ChevronLeft size={24} className="text-white" />
                </button>
                <button
                  onClick={nextVideo}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/30 hover:bg-white/50 p-2 rounded-full shadow-lg transition-all"
                  aria-label="Next video"
                >
                  <ChevronRight size={24} className="text-white" />
                </button>
              </>
            )}
          </div>

          {/* Video indicators - Only show for mailbox type with multiple videos */}
          {pageConfig.videos.length > 1 && (
            <div className="flex justify-center gap-2 mt-4">
              {pageConfig.videos.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentVideoIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentVideoIndex
                      ? "bg-blue-600 w-6"
                      : "bg-gray-300 hover:bg-gray-400"
                  }`}
                  aria-label={`Go to video ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InitialPageVisitGuideModal;
